/**
 * Verification Reasons aggregation — derived client-side from
 * `CaseSummary.follow_up_reason` + `created_at`. When the backend exposes
 * a first-class `verification_reason` + `verification_reason_created_at`
 * pair, this helper continues to work as a fallback.
 *
 * See docs/CLAUDE-verification-center-layout-2026-05-29.md for the
 * 6-source → 5-display reason bucketing rationale.
 */

import type { CaseSummary } from "@/lib/api";
import { getCaseStatus } from "@/lib/caseStatus";

export type ReasonWindow = 7 | 30 | 90 | "all";

export interface ReasonRow {
  key: string;
  label: string;
  count: number;
  percent: number;
}

export interface ReviewOutcomes {
  verified: number;
  not_verified: number;
  pending_review: number;
  still_open: number;
  dropped_off: number;
  total: number;
}

export interface VerificationReasonsResult {
  rows: ReasonRow[];
  total: number;
  topLabel: string | null;
  windowLabel: string;
  outcomes: ReviewOutcomes;
}

// Display category order (matches the mock).
const DISPLAY_ORDER: { key: string; label: string }[] = [
  { key: "limited_identity_history", label: "Limited identity history" },
  { key: "limited_us_history", label: "Limited U.S. history" },
  { key: "identity_signals_incomplete", label: "Identity signals incomplete" },
  { key: "name_email_mismatch", label: "Name/email mismatch" },
  { key: "standard_identity_review", label: "Standard identity review" },
];

// Map backend follow_up_reason `value` → display category key.
// Unknown / empty / fallback values bucket into "standard_identity_review".
const REASON_TO_DISPLAY: Record<string, string> = {
  thin_credit_file: "limited_identity_history",
  new_to_country: "limited_us_history",
  additional_identity_confidence: "identity_signals_incomplete",
  name_contact_mismatch: "name_email_mismatch",
  manual_verification: "standard_identity_review",
  application_clarification: "standard_identity_review",
};

/** Bucket a raw backend `follow_up_reason` value into one of the 5 display keys. */
export function bucketReason(raw: string | null | undefined): string {
  const v = (raw ?? "").trim();
  return REASON_TO_DISPLAY[v] ?? "standard_identity_review";
}

/** Display label for a bucket key. */
export function reasonBucketLabel(key: string): string {
  return DISPLAY_ORDER.find((d) => d.key === key)?.label ?? key;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function windowLabelFor(w: ReasonWindow): string {
  if (w === "all") return "All time";
  if (w === 7) return "Last 7 days";
  if (w === 90) return "Last 90 days";
  return "Last 30 days";
}

export function computeVerificationReasons(
  cases: CaseSummary[],
  window: ReasonWindow,
): VerificationReasonsResult {
  const now = Date.now();
  const cutoff =
    window === "all" ? null : now - (window as number) * DAY_MS;

  const counts: Record<string, number> = Object.fromEntries(
    DISPLAY_ORDER.map((d) => [d.key, 0]),
  );

  const outcomes: ReviewOutcomes = {
    verified: 0,
    not_verified: 0,
    pending_review: 0,
    still_open: 0,
    dropped_off: 0,
    total: 0,
  };

  let total = 0;
  for (const c of cases) {
    if (cutoff !== null) {
      const t = c.created_at ? new Date(c.created_at).getTime() : NaN;
      if (!Number.isFinite(t) || t < cutoff) continue;
    }
    const raw = (c.follow_up_reason ?? "").trim();
    const key = REASON_TO_DISPLAY[raw] ?? "standard_identity_review";
    counts[key] += 1;
    total += 1;

    // Outcome bucketing — keyed off the same case-status pill the operator
    // sees in the queue, so the numbers always match the table.
    // Dropped Off (closed) cases are tracked but EXCLUDED from `outcomes.total`
    // so the percentage breakdown reflects only active/decided applicants.
    const kind = getCaseStatus(c).kind;
    if (kind === "verified") {
      outcomes.verified += 1;
      outcomes.total += 1;
    } else if (kind === "not_verified") {
      outcomes.not_verified += 1;
      outcomes.total += 1;
    } else if (kind === "submitted") {
      outcomes.pending_review += 1;
      outcomes.total += 1;
    } else if (kind === "closed") {
      outcomes.dropped_off += 1;
      // intentionally NOT added to outcomes.total
    } else if (kind === "pending" || kind === "in_progress" || kind === "action_required") {
      outcomes.still_open += 1;
      outcomes.total += 1;
    }
  }

  const rows: ReasonRow[] = DISPLAY_ORDER.map(({ key, label }) => ({
    key,
    label,
    count: counts[key],
    percent: total > 0 ? Math.round((counts[key] / total) * 100) : 0,
  }));

  const topRow = total > 0
    ? rows.reduce((best, r) => (r.count > best.count ? r : best), rows[0])
    : null;

  return {
    rows,
    total,
    topLabel: topRow && topRow.count > 0 ? topRow.label : null,
    windowLabel: windowLabelFor(window),
    outcomes,
  };
}
